import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Plus, Trash2, Globe, CheckCircle2, Clock, WifiOff, ShieldQuestion } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

/** /app/sso-rollout — admin tracker for SocialPulse SSO readiness across network sites. */
export default function SsoRollout() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bulk, setBulk] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/admin/sso-rollout/status");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function refresh() {
    setRefreshing(true);
    try {
      await api.post("/v1/admin/sso-rollout/refresh");
      toast.success("All sites re-checked.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  }

  async function addSites(e) {
    e.preventDefault();
    const sites = bulk
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const [slug, base_url, ...rest] = l.split(",").map((p) => p.trim());
        return { slug, base_url, name: rest.join(", ") || undefined };
      })
      .filter((s) => s.slug && s.base_url);
    if (!sites.length) {
      toast.error("One site per line: slug, https://base-url, optional name");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post("/v1/admin/sso-rollout/sites", { sites });
      toast.success(`Saved: ${data.upserted.join(", ")}`);
      setBulk("");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setAdding(false);
    }
  }

  async function removeSite(slug) {
    if (!confirm(`Remove ${slug} from the tracker?`)) return;
    try {
      await api.delete(`/v1/admin/sso-rollout/sites/${slug}`);
      toast.success("Removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (user && user.role !== "admin") {
    return (
      <section className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="heritage-title text-3xl">Admins only.</h1>
        <p className="mt-3 text-sm text-[color:var(--ink-soft)]">The rollout desk is restricted to operators.</p>
      </section>
    );
  }

  const sites = data?.sites || [];
  const summary = data?.summary || { total: 0, live: 0, pending: 0, unreachable: 0, unchecked: 0 };
  const pct = summary.total ? Math.round((summary.live / summary.total) * 100) : 0;

  return (
    <section className="mx-auto max-w-6xl px-6 lg:px-10 pt-14 pb-24" data-testid="sso-rollout-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="overline mb-3">Operations · Network</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-[0.95] tracking-tight">
            SocialPulse SSO rollout
          </h1>
          <p className="mt-3 text-sm text-[color:var(--ink-soft)] max-w-xl">
            One sign-in across every site. A site is <strong>live</strong> the moment its
            SSO credentials are in place and its status check answers configured.
          </p>
        </div>
        <button onClick={refresh} disabled={refreshing} data-testid="sso-refresh-btn" className="btn-rust disabled:opacity-50">
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Checking…" : "Re-check all sites"}
        </button>
      </div>

      {/* Progress */}
      <div className="border border-[color:var(--ink-line)] p-6 mb-10" data-testid="sso-progress">
        <div className="flex items-baseline justify-between mb-3">
          <div className="overline">Network readiness</div>
          <div className="font-serif-display text-3xl tabular-nums" data-testid="sso-progress-count">
            {summary.live} / {summary.total} live
          </div>
        </div>
        <div className="h-2 bg-[color:var(--parchment-2)] border border-[color:var(--ink-line)]">
          <div
            className="h-full bg-[color:var(--rust)] transition-all duration-700"
            style={{ width: `${pct}%` }}
            data-testid="sso-progress-bar"
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[color:var(--ink-soft)]">
          <span data-testid="sso-count-pending">{summary.pending} awaiting credentials</span>
          <span data-testid="sso-count-unreachable">{summary.unreachable} unreachable</span>
          <span data-testid="sso-count-unchecked">{summary.unchecked} not yet checked</span>
        </div>
      </div>

      {/* Site grid */}
      {loading ? (
        <div className="text-sm text-[color:var(--ink-soft)]">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-[color:var(--ink-line)]" data-testid="sso-site-grid">
          {sites.map((s) => {
            const state = s.configured === true ? "live" : s.reachable === false ? "unreachable" : s.configured === false ? "pending" : "unchecked";
            const chip = {
              live: ["LIVE", "text-[#3d5a3c] border-[#3d5a3c]", CheckCircle2],
              pending: ["AWAITING CREDENTIALS", "text-[color:var(--rust)] border-[color:var(--rust)]/60", Clock],
              unreachable: ["UNREACHABLE", "text-[color:var(--ink-soft)] border-[color:var(--ink-soft)]/50", WifiOff],
              unchecked: ["NOT CHECKED", "text-[color:var(--ink-soft)] border-[color:var(--ink-soft)]/50", ShieldQuestion],
            }[state];
            const Icon = chip[2];
            return (
              <div key={s.slug} className="border-r border-b border-[color:var(--ink-line)] p-5 flex flex-col gap-3" data-testid={`sso-site-${s.slug}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif-display text-xl leading-tight">{s.name}</div>
                    <a href={s.base_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs link-underline text-[color:var(--ink-soft)]">
                      <Globe size={10} /> {s.base_url.replace(/^https?:\/\//, "")}
                    </a>
                  </div>
                  <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 font-heritage-caps text-[9px] tracking-[0.12em] whitespace-nowrap ${chip[1]}`} data-testid={`sso-site-status-${s.slug}`}>
                    <Icon size={10} /> {chip[0]}
                  </span>
                </div>
                <div className="mt-auto flex items-center justify-between text-[11px] text-[color:var(--ink-soft)]">
                  <span>{s.checked_at ? `checked ${new Date(s.checked_at).toLocaleString()}` : "never checked"}</span>
                  <button onClick={() => removeSite(s.slug)} data-testid={`sso-site-remove-${s.slug}`} className="hover:text-[color:var(--rust)]" title="Remove from tracker">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk add */}
      <form onSubmit={addSites} className="mt-10 border border-[color:var(--ink-line)] p-6" data-testid="sso-add-form">
        <div className="overline mb-2 flex items-center gap-2"><Plus size={12} /> Add or update sites</div>
        <p className="text-xs text-[color:var(--ink-soft)] mb-3">
          One per line: <code>slug, https://base-url, optional display name</code>. Existing slugs are updated in place.
        </p>
        <textarea
          rows={4}
          value={bulk}
          onChange={(e) => setBulk(e.target.value)}
          data-testid="sso-add-input"
          placeholder={"familytree, https://familytree.team, FamilyTree.Team\nfoodpulse, https://foodpulse.net, Food Pulse"}
          className="w-full bg-transparent border border-[color:var(--sepia)]/40 p-3 text-sm font-mono focus:outline-none focus:border-[color:var(--rust)]"
        />
        <button type="submit" disabled={adding || !bulk.trim()} data-testid="sso-add-submit" className="btn-wood mt-4 !py-2 !px-5 !text-xs disabled:opacity-50">
          {adding ? "Saving…" : "Save sites"}
        </button>
      </form>
    </section>
  );
}
